import App from "../../../vendor_core/core/src/App";
import SeamlessAudioSequencer from "../../../vendor_core/core/src/utils/SeamlessAudioSequencer";
import GameEvents from "../../../vendor_core/core/src/const/GameEvents";
import SpinModel from "../../../vendor_core/core/src/model/SpinModel";
import ConsoleUtil from "../../../vendor_core/core/src/utils/ConsoleUtil";
import { GameHowl } from "../../../vendor_core/core/src/utils/GameHowl";
import StateMachine from "../../../vendor_core/core/src/states/StateMachine";
import Utils from "../../../vendor_core/core/src/utils/Utils";
import GameModel from "../../../vendor_core/core/src/model/GameModel";

export default class IdleBackgroundMusicSequencer extends SeamlessAudioSequencer {
    private crossFadeTimeInSeconds: number[] = [
        1.0,
        1.0,
        1.0,
        1.0
    ];

    private winStates: Map<string, number>;

    private winRatiosDuringLastLoop: Set<number>;
    private loopEndCallback: VoidFunction;
    private debugLogEnabled: boolean;

    private bigWinSkipped: boolean = false;

    public async init(masterSoundId: string, tracks: GameHowl[], bpm: number, bpsMultiplier: number = 1) {
       super.init(masterSoundId, tracks, bpm, bpsMultiplier);

       this.debugLogEnabled = GameModel.urlParams.showMixer === 'true';
       this.winStates = new Map<string, number>();
       this.winStates.set("show_win", 0);
       this.winStates.set("big_win", 3);


        this.winRatiosDuringLastLoop = new Set<number>();
        App.dispatcher.on(GameEvents.FREE_SPIN_OUTRO_CLOSED, async () => {
            this.debugLog(`change track directly to 0`);
            this.clearCandidates();
            this.changeTrack(0, 0);
        });
        
        App.dispatcher.on(GameEvents.BIG_WIN_SKIPPED, async () => {
            if (SpinModel.featureMode > -1 || SpinModel.hasFreespinsWon()) {
                this.debugLog(`big win skip ignored. Reason freespins are upcoming or currently playing.`);
                // We do not change tracks during free spins
                return;
            }

            this.bigWinSkipped = true;
            await this.applyTrackChangeLogic();
        }, this);

        App.dispatcher.on(GameEvents.STATE_CHANGED, async (enterStateName: string, exitStateName: string) => {
            if (enterStateName == "enter_freespins") {
                this.sequence.tracks.forEach(track => {
                    track.stop();
                });

                return;
            }

            if (SpinModel.featureMode > -1 || SpinModel.hasFreespinsWon()) {
                this.debugLog(`state change ignored. Reason freespins are upcoming or currently playing.`);
                // We do not change tracks during free spins
                return;
            }

            if (enterStateName == "big_win") {
                this.bigWinSkipped = false;
                await Utils.wait(1.0);
                this.addNextTrackCandidate(3);

                return;
            }

            if (!SpinModel.hasBigWin()) {
                if (exitStateName == "show_win") {
                    this.debugLog("exiting show_win state");
                    await this.applyTrackChangeLogic();
                }
            } else {
                if (exitStateName == "big_win" && this.bigWinSkipped == false) {
                    await this.applyTrackChangeLogic();
                }
            }
        }, this);

        this.loopEndCallback = this.onTrackEnd.bind(this);
        this.sequence.tracks.forEach(track => {
            track.on("end", this.loopEndCallback);
        })
    }


    private async applyTrackChangeLogic() {
        const winRatio = SpinModel.getWinRatio();

        let trackIndex: number = 0;
        if (winRatio > 20) {
            trackIndex = 3;
        } else if (winRatio >= 5) {
            trackIndex = 2;
        } else if (winRatio >= 0.5) {
            trackIndex = 1;
        }

        trackIndex = Math.max(trackIndex, this.getBestCandidate());

        if (trackIndex > this.sequence.trackIndex) {
            // We need to switch to the more dramatic music as soon as possible.
            await this.changeTrackInternal(trackIndex);
        } else {
            this.addNextTrackCandidate(trackIndex);
            this.debugLog(`Collect win ratio ${trackIndex}`);
        }
    }

    private async changeTrackInternal(trackIndex: number) {
        this.debugLog(`Changing track internal ${trackIndex}`);
        this.changeTrack(trackIndex, this.crossFadeTimeInSeconds[trackIndex]);
        this.clearCandidates();
    }

    private async onTrackEnd(id: number) {
        const endedTrack = this.sequence.tracks.find(track => track.id == id);
        const endedTrackIndex = this.sequence.tracks.indexOf(endedTrack);
        if (endedTrackIndex != this.trackIndex) {
            endedTrack.stop();
            this.debugLog(`track ${endedTrackIndex} just ended. Stopping it.`);

            return;
        }

        if (this.winStates.has(StateMachine.currentState.name)) {
            this.debugLog(`track ${this.trackIndex} end ignored. We are in state ${StateMachine.currentState.name}`);
            this.addNextTrackCandidate(this.winStates.get(StateMachine.currentState.name));
            return
        }

        if (SpinModel.hasFreespinsWon()) {
            this.debugLog(`track ${this.trackIndex} end ignored. We won free spins`);
            this.clearCandidates();
            this.addNextTrackCandidate(0);
            return;
        }

        this.addNextTrackCandidate(0);
        const nextTrackIndex = this.getBestCandidate();
        if (this.sequence.trackIndex != nextTrackIndex) {
            this.debugLog(`track ${this.trackIndex} end, next track ${nextTrackIndex}`);
            await this.changeTrackInternal(nextTrackIndex);
        } else {
            this.debugLog(`track ${nextTrackIndex} retrigger`);
            this.clearCandidates();
        }
    }

    private debugLog(message: string) {
        if (this.debugLogEnabled) {
            ConsoleUtil.log(`[Audio Sequencer] [${new Date().toLocaleTimeString()}] ${message}`)
        }
    }

    private addNextTrackCandidate(trackIndex: number) {
        this.debugLog("Adding candidate " + trackIndex);
        this.winRatiosDuringLastLoop.add(trackIndex);
        this.getBestCandidate(); // Just to log the data
    }

    private getBestCandidate() {
        const values = Array.from(this.winRatiosDuringLastLoop.values());
        const nextTrackIndex = Math.max(...values);
        if (nextTrackIndex > -1) {
            this.debugLog("Best candidate so far " + nextTrackIndex);
        }

        return nextTrackIndex;
    }

    private clearCandidates() {
        this.debugLog("Clearing next track candidates");
        this.winRatiosDuringLastLoop.clear();
    }
}